import { useCallback, useMemo, useState } from 'react';
import type { SingleValue } from 'react-select';

import type { User } from '@/api/v4/user.api';
import { useRoles } from '@/hooks/queries/use-roles.query';
import { APP_USER_ROLES } from '@/types';
import type { SelectOptionValue } from '@/ui/select/select';
import { Select } from '@/ui/select/select';

import { type UserRoleOption } from './settings.state';
import { fetchSharedCompanies } from '@/api/v4/shared-organizations';
import { fetchSharedProjects } from '@/api/v4/shared-projects';
import { useSetAtom } from 'jotai';
import { modalState } from '@/state/modal.state';
import { useRolesOptions } from '@/hooks/use-roles-options';

type RoleDropdownSelectProps = {
  user: User;
  isDisabled?: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
};

export const RoleDropdownSelect = ({
  user,
  isDisabled,
  setIsProcessing,
}: RoleDropdownSelectProps) => {
  const currentUserRoleOption = useMemo(
    () => ({
      label: user.role.name,
      value: user.role.id,
    }),
    [user],
  );

  const [selectedRole, setSelectedRole] = useState(currentUserRoleOption);

  const { data: appRoles } = useRoles();
  const rolesOptions = useRolesOptions();

  const setModal = useSetAtom(modalState);

  const getUserRole = useCallback(
    (roleId: string): UserRoleOption => {
      const appRoleById = appRoles?.find(role => role.id === roleId);

      const guestRole = appRoles?.find(
        role => role.name === APP_USER_ROLES.GUEST,
      );

      return {
        label: (appRoleById?.name as APP_USER_ROLES) || APP_USER_ROLES.GUEST,
        id: appRoleById?.id || guestRole?.id || '',
      };
    },
    [appRoles],
  );

  const getLinkedEntitiesStatus = async (email: string) => {
    const sharedCompanies = await fetchSharedCompanies({
      uid: email,
      items: 20,
      page: 1,
      explicitlySharedOnly: true,
    });

    const sharedProjects = await fetchSharedProjects({
      uid: email,
      items: 20,
      page: 1,
      explicitlySharedOnly: true,
    });

    return sharedCompanies.length > 0 || sharedProjects.length > 0;
  };

  const openChangeUserRoleModalAndStartProcessingChange = useCallback(
    async (role: UserRoleOption, user: User) => {
      setIsProcessing(true);
      const hasLinkedEntities = await getLinkedEntitiesStatus(user.email);

      setModal({
        state: 'changeUserRole',
        userId: user.id,
        userDisplayName: `${user.firstName} ${user.lastName}`,
        currentRole: getUserRole(user.role.id),
        newRole: getUserRole(role.id),
        hasLinkedEntities,
        onCancel: () => {
          setSelectedRole(currentUserRoleOption);
        },
      });
      setIsProcessing(false);
    },
    [getUserRole, setModal, setIsProcessing, currentUserRoleOption],
  );

  const selectNewRole = async (v: SingleValue<SelectOptionValue<string>>) => {
    if (v?.value === user.role.id || !v?.value) return;

    setSelectedRole({
      label: getUserRole(v.value).label,
      value: v.value,
    });

    await openChangeUserRoleModalAndStartProcessingChange(
      getUserRole(v.value),
      user,
    );
  };

  return (
    <Select
      value={selectedRole}
      isDisabled={isDisabled}
      menuPortalTarget={document.body}
      onChange={selectNewRole}
      options={rolesOptions}
      styles={{
        menu: base => ({
          ...base,
          [`@media (max-width: 1180px)`]: {
            width: 'min-content',
          },
        }),
        singleValue: base => ({
          ...base,
          '.hidden-inside-single-value': {
            display: 'none',
          },
        }),
      }}
    />
  );
};
